import { useState, useEffect, useRef } from 'react';
import {
    Kokki, KokkiEmote, KokkiPowerup, KokkiSkin,
    PowerupTemplate,
    CreateKokkiDto, UpdateKokkiDto,
    CreateKokkiEmoteDto, CreateKokkiPowerupDto,
    CreateKokkiSkinDto, UpdateKokkiSkinDto,
    fetchAllKokkis, createKokki, updateKokki, deleteKokki,
    createEmote, deleteEmote,
    assignPowerup, updatePowerup, deletePowerup,
    fetchAllTemplates,
    createSkin, updateSkin, deleteSkin,
    uploadFile,
} from '../api/kokki';

const C = {
    primary: '#10b981', secondary: '#3b82f6', accent: '#8b5cf6',
    warning: '#f59e0b', success: '#22c55e', danger: '#ef4444',
};

const GAME_TYPES = [
    { value: 'flipCardGame', label: '🃏 Bandido' },
    { value: 'quizMarathon', label: '🏃 Zooma' },
    { value: 'doubleDown', label: '🎰 Rolla' },
] as const;

const inputStyle: React.CSSProperties = { width: '100%', padding: '12px', background: '#0f172a', border: '1px solid #334155', borderRadius: '10px', color: '#f1f5f9', fontSize: '14px', boxSizing: 'border-box' };
const labelStyle: React.CSSProperties = { color: '#94a3b8', fontSize: '12px', marginBottom: '6px', display: 'block', fontWeight: '600' };
const btnStyle = (bg: string, color = '#fff'): React.CSSProperties => ({ padding: '10px 18px', background: bg, color, border: 'none', borderRadius: '10px', fontSize: '13px', cursor: 'pointer', fontWeight: '600' });

// ==================== IMAGE UPLOAD BUTTON ====================

const ImageUploadButton = ({ onUploaded, label = 'Upload Image' }: { onUploaded: (url: string) => void; label?: string }) => {
    const ref = useRef<HTMLInputElement>(null);
    const [uploading, setUploading] = useState(false);

    const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        try {
            setUploading(true);
            const { url } = await uploadFile(file);
            onUploaded(url);
        } catch { alert('Upload failed'); }
        finally { setUploading(false); if (ref.current) ref.current.value = ''; }
    };

    return (
        <>
            <input ref={ref} type="file" accept="image/*,.gif,.svg" onChange={handleFile} style={{ display: 'none' }} />
            <button type="button" onClick={() => ref.current?.click()} disabled={uploading} style={{ ...btnStyle(uploading ? '#334155' : C.secondary), fontSize: '12px', padding: '8px 14px' }}>
                {uploading ? '⏳ Uploading...' : `📁 ${label}`}
            </button>
        </>
    );
};

// ==================== DELETE MODAL ====================

const DeleteModal = ({ title, name, onConfirm, onClose, loading }: { title: string; name: string; onConfirm: () => void; onClose: () => void; loading: boolean }) => (
    <div onClick={(e) => e.target === e.currentTarget && onClose()} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '24px' }}>
        <div style={{ background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', borderRadius: '20px', border: '1px solid #334155', width: '100%', maxWidth: '450px', padding: '32px', textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🗑️</div>
            <h2 style={{ color: '#f1f5f9', fontSize: '20px', fontWeight: '700', marginBottom: '8px' }}>Delete {title}?</h2>
            <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '24px' }}>Are you sure you want to delete "<span style={{ color: '#f1f5f9' }}>{name}</span>"? This cannot be undone.</p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                <button onClick={onClose} style={btnStyle('#334155', '#f1f5f9')}>Cancel</button>
                <button onClick={onConfirm} disabled={loading} style={btnStyle(loading ? '#334155' : C.danger)}>
                    {loading ? 'Deleting...' : 'Delete'}
                </button>
            </div>
        </div>
    </div>
);

// ==================== KOKKI FORM MODAL ====================

interface KokkiFormProps {
    kokki?: Kokki | null;
    onSave: (data: CreateKokkiDto | UpdateKokkiDto) => void;
    onClose: () => void;
    loading: boolean;
}

const KokkiFormModal = ({ kokki, onSave, onClose, loading }: KokkiFormProps) => {
    const [form, setForm] = useState<CreateKokkiDto>({
        name: kokki?.name || '',
        imageUrl: kokki?.imageUrl || '',
        story: kokki?.story || '',
        description: kokki?.description || '',
        characteristics: kokki?.characteristics || '',
    });

    const handleSubmit = (e: React.FormEvent) => { e.preventDefault(); onSave(form); };

    return (
        <div onClick={(e) => e.target === e.currentTarget && onClose()} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '24px' }}>
            <div style={{ background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', borderRadius: '20px', border: '1px solid #334155', width: '100%', maxWidth: '650px', maxHeight: '90vh', overflow: 'auto' }}>
                <div style={{ padding: '24px', borderBottom: '1px solid #334155', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h2 style={{ color: '#f1f5f9', fontSize: '20px', fontWeight: '700' }}>{kokki ? '✏️ Edit Kokki' : '➕ Create Kokki'}</h2>
                    <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#94a3b8', fontSize: '28px', cursor: 'pointer' }}>×</button>
                </div>
                <form onSubmit={handleSubmit} style={{ padding: '24px' }}>
                    <div style={{ display: 'grid', gap: '18px' }}>
                        <div>
                            <label style={labelStyle}>Name *</label>
                            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required style={inputStyle} placeholder="e.g. Blaze" />
                        </div>
                        <div>
                            <label style={labelStyle}>Avatar Image</label>
                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                <input value={form.imageUrl} onChange={(e) => setForm({ ...form, imageUrl: e.target.value })} style={{ ...inputStyle, flex: 1 }} placeholder="URL or upload" />
                                <ImageUploadButton onUploaded={(url) => setForm({ ...form, imageUrl: url })} />
                            </div>
                            {form.imageUrl && <img src={form.imageUrl?.startsWith('/') ? `${(process.env.REACT_APP_API_URL || 'https://api.playkokki.com').replace(/\/$/, '')}${form.imageUrl}` : form.imageUrl} alt="preview" style={{ marginTop: '10px', width: '80px', height: '80px', objectFit: 'cover', borderRadius: '12px', border: '2px solid #334155' }} onError={(e) => (e.currentTarget.style.display = 'none')} />}
                        </div>
                        <div>
                            <label style={labelStyle}>Description</label>
                            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }} placeholder="Short description" />
                        </div>
                        <div>
                            <label style={labelStyle}>Story / Lore</label>
                            <textarea value={form.story} onChange={(e) => setForm({ ...form, story: e.target.value })} style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }} placeholder="Backstory..." />
                        </div>
                        <div>
                            <label style={labelStyle}>Characteristics</label>
                            <textarea value={form.characteristics} onChange={(e) => setForm({ ...form, characteristics: e.target.value })} style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }} placeholder="Brave, clever, mischievous..." />
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #334155' }}>
                        <button type="button" onClick={onClose} style={btnStyle('#334155', '#f1f5f9')}>Cancel</button>
                        <button type="submit" disabled={loading || !form.name} style={btnStyle(loading || !form.name ? '#334155' : C.primary, loading || !form.name ? '#64748b' : '#0f172a')}>
                            {loading ? 'Saving...' : kokki ? 'Update' : 'Create'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// ==================== ADD EMOTE MODAL ====================

const AddEmoteModal = ({ kokkiId, onSave, onClose, loading }: { kokkiId: number; onSave: (d: CreateKokkiEmoteDto) => void; onClose: () => void; loading: boolean }) => {
    const [name, setName] = useState('');
    const [imageUrl, setImageUrl] = useState('');

    return (
        <div onClick={(e) => e.target === e.currentTarget && onClose()} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '24px' }}>
            <div style={{ background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', borderRadius: '20px', border: '1px solid #334155', width: '100%', maxWidth: '500px', padding: '32px' }}>
                <h2 style={{ color: '#f1f5f9', fontSize: '20px', fontWeight: '700', marginBottom: '20px' }}>😄 Add Emote</h2>
                <div style={{ display: 'grid', gap: '16px' }}>
                    <div>
                        <label style={labelStyle}>Name *</label>
                        <input value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} placeholder="e.g. happy, sad, celebrate" />
                    </div>
                    <div>
                        <label style={labelStyle}>Image (can be GIF)</label>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                            <input value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} style={{ ...inputStyle, flex: 1 }} placeholder="URL or upload" />
                            <ImageUploadButton onUploaded={setImageUrl} label="Upload" />
                        </div>
                        {imageUrl && <img src={imageUrl.startsWith('/') ? `${(process.env.REACT_APP_API_URL || 'https://api.playkokki.com').replace(/\/$/, '')}${imageUrl}` : imageUrl} alt="preview" style={{ marginTop: '8px', width: '60px', height: '60px', objectFit: 'cover', borderRadius: '8px' }} onError={(e) => (e.currentTarget.style.display = 'none')} />}
                    </div>
                </div>
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
                    <button onClick={onClose} style={btnStyle('#334155', '#f1f5f9')}>Cancel</button>
                    <button onClick={() => onSave({ name, imageUrl: imageUrl || '', kokkiId })} disabled={loading || !name} style={btnStyle(loading || !name ? '#334155' : C.primary, loading || !name ? '#64748b' : '#0f172a')}>
                        {loading ? 'Saving...' : 'Add Emote'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ==================== ASSIGN POWERUP MODAL ====================

interface AssignPowerupProps {
    kokkiId: number;
    existingPowerups: KokkiPowerup[];
    onSave: (d: CreateKokkiPowerupDto) => void;
    onClose: () => void;
    loading: boolean;
}

const AssignPowerupModal = ({ kokkiId, existingPowerups, onSave, onClose, loading }: AssignPowerupProps) => {
    const [templates, setTemplates] = useState<PowerupTemplate[]>([]);
    const [filterGame, setFilterGame] = useState<string>('all');
    const [charges, setCharges] = useState(1);
    const [loadingT, setLoadingT] = useState(true);

    useEffect(() => {
        fetchAllTemplates().then(setTemplates).catch(() => {}).finally(() => setLoadingT(false));
    }, []);

    const assignedIds = new Set(existingPowerups.map(p => p.templateId));
    const available = templates.filter(t => !assignedIds.has(t.id) && (filterGame === 'all' || t.gameType === filterGame));

    return (
        <div onClick={(e) => e.target === e.currentTarget && onClose()} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '24px' }}>
            <div style={{ background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', borderRadius: '20px', border: '1px solid #334155', width: '100%', maxWidth: '650px', maxHeight: '85vh', overflow: 'auto', padding: '32px' }}>
                <h2 style={{ color: '#f1f5f9', fontSize: '20px', fontWeight: '700', marginBottom: '16px' }}>⚡ Assign Powerup from Bank</h2>
                <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', alignItems: 'center' }}>
                    <select value={filterGame} onChange={(e) => setFilterGame(e.target.value)} style={{ ...inputStyle, maxWidth: '200px' }}>
                        <option value="all">All Games</option>
                        {GAME_TYPES.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                    </select>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <label style={{ ...labelStyle, marginBottom: 0 }}>Charges:</label>
                        <input type="number" min={1} max={10} value={charges} onChange={(e) => setCharges(Math.max(1, +e.target.value))} style={{ ...inputStyle, width: '70px', textAlign: 'center' }} />
                    </div>
                </div>
                {loadingT ? <div style={{ color: '#64748b', textAlign: 'center', padding: '20px' }}>Loading templates...</div> : available.length === 0 ? (
                    <div style={{ color: '#64748b', textAlign: 'center', padding: '20px' }}>No available templates{filterGame !== 'all' ? ' for this game' : ''}</div>
                ) : (
                    <div style={{ display: 'grid', gap: '8px', maxHeight: '400px', overflow: 'auto' }}>
                        {available.map(t => {
                            const gameLabel = GAME_TYPES.find(g => g.value === t.gameType)?.label || t.gameType;
                            return (
                                <div key={t.id} style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '12px', padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div>
                                        <span style={{ color: t.type === 'ultimate' ? '#f59e0b' : '#10b981', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', marginRight: '8px' }}>{t.type}</span>
                                        <span style={{ color: '#f1f5f9', fontSize: '14px', fontWeight: '600' }}>{t.name}</span>
                                        <div style={{ color: '#64748b', fontSize: '11px', marginTop: '4px' }}>{gameLabel} • {t.effectKey}</div>
                                        {t.description && <div style={{ color: '#94a3b8', fontSize: '12px', marginTop: '4px' }}>{t.description}</div>}
                                    </div>
                                    <button onClick={() => onSave({ kokkiId, templateId: t.id, charges })} disabled={loading} style={{ ...btnStyle(loading ? '#334155' : C.primary, loading ? '#64748b' : '#0f172a'), fontSize: '12px', padding: '8px 14px', whiteSpace: 'nowrap' }}>
                                        {loading ? '...' : '➕ Assign'}
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                    <button onClick={onClose} style={btnStyle('#334155', '#f1f5f9')}>Close</button>
                </div>
            </div>
        </div>
    );
};

// ==================== ADD/EDIT SKIN MODAL ====================

interface SkinFormProps {
    kokkiId: number;
    skin?: KokkiSkin | null;
    onSave: (d: CreateKokkiSkinDto | UpdateKokkiSkinDto) => void;
    onClose: () => void;
    loading: boolean;
}

const SkinFormModal = ({ kokkiId, skin, onSave, onClose, loading }: SkinFormProps) => {
    const [name, setName] = useState(skin?.name || '');
    const [imageUrl, setImageUrl] = useState(skin?.imageUrl || '');

    const handleSubmit = () => {
        if (skin) { onSave({ name, imageUrl } as UpdateKokkiSkinDto); }
        else { onSave({ name, imageUrl, kokkiId } as CreateKokkiSkinDto); }
    };

    return (
        <div onClick={(e) => e.target === e.currentTarget && onClose()} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '24px' }}>
            <div style={{ background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', borderRadius: '20px', border: '1px solid #334155', width: '100%', maxWidth: '500px', padding: '32px' }}>
                <h2 style={{ color: '#f1f5f9', fontSize: '20px', fontWeight: '700', marginBottom: '20px' }}>{skin ? '✏️ Edit Skin' : '🎨 Add Skin'}</h2>
                <div style={{ display: 'grid', gap: '16px' }}>
                    <div>
                        <label style={labelStyle}>Name *</label>
                        <input value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} placeholder="e.g. Golden, Dark, Neon" />
                    </div>
                    <div>
                        <label style={labelStyle}>Skin Image</label>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                            <input value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} style={{ ...inputStyle, flex: 1 }} placeholder="URL or upload" />
                            <ImageUploadButton onUploaded={setImageUrl} label="Upload" />
                        </div>
                        {imageUrl && <img src={imageUrl.startsWith('/') ? `${(process.env.REACT_APP_API_URL || 'https://api.playkokki.com').replace(/\/$/, '')}${imageUrl}` : imageUrl} alt="preview" style={{ marginTop: '8px', width: '60px', height: '60px', objectFit: 'cover', borderRadius: '8px' }} onError={(e) => (e.currentTarget.style.display = 'none')} />}
                    </div>
                </div>
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
                    <button onClick={onClose} style={btnStyle('#334155', '#f1f5f9')}>Cancel</button>
                    <button onClick={handleSubmit} disabled={loading || !name} style={btnStyle(loading || !name ? '#334155' : C.primary, loading || !name ? '#64748b' : '#0f172a')}>
                        {loading ? 'Saving...' : skin ? 'Update' : 'Add Skin'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ==================== KOKKI DETAIL PANEL ====================

const resolveUrl = (url: string | null | undefined) => {
    if (!url) return '';
    if (url.startsWith('/')) return `${(process.env.REACT_APP_API_URL || 'https://api.playkokki.com').replace(/\/$/, '')}${url}`;
    return url;
};

interface DetailPanelProps {
    kokki: Kokki;
    onAddEmote: () => void;
    onDeleteEmote: (e: KokkiEmote) => void;
    onAddPowerup: () => void;
    onUpdateCharges: (p: KokkiPowerup, charges: number) => void;
    onDeletePowerup: (p: KokkiPowerup) => void;
    onAddSkin: () => void;
    onEditSkin: (s: KokkiSkin) => void;
    onDeleteSkin: (s: KokkiSkin) => void;
    onClose: () => void;
}

const KokkiDetailPanel = ({ kokki, onAddEmote, onDeleteEmote, onAddPowerup, onUpdateCharges, onDeletePowerup, onAddSkin, onEditSkin, onDeleteSkin, onClose }: DetailPanelProps) => {
    const grouped = GAME_TYPES.map(gt => ({
        ...gt,
        normals: kokki.powerups.filter(p => p.template.gameType === gt.value && p.template.type === 'normal'),
        ultimate: kokki.powerups.find(p => p.template.gameType === gt.value && p.template.type === 'ultimate'),
    }));

    return (
        <div onClick={(e) => e.target === e.currentTarget && onClose()} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '24px' }}>
            <div style={{ background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', borderRadius: '20px', border: '1px solid #334155', width: '100%', maxWidth: '800px', maxHeight: '90vh', overflow: 'auto' }}>
                {/* Header */}
                <div style={{ padding: '24px', borderBottom: '1px solid #334155', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                        {kokki.imageUrl ? <img src={resolveUrl(kokki.imageUrl)} alt={kokki.name} style={{ width: '60px', height: '60px', borderRadius: '14px', objectFit: 'cover', border: '2px solid #334155' }} /> : <div style={{ width: '60px', height: '60px', background: '#334155', borderRadius: '14px', display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: '28px' }}>🍪</div>}
                        <div>
                            <h2 style={{ color: '#f1f5f9', fontSize: '22px', fontWeight: '700' }}>{kokki.name}</h2>
                            <p style={{ color: '#64748b', fontSize: '13px' }}>{kokki.description || 'No description'}</p>
                        </div>
                    </div>
                    <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#94a3b8', fontSize: '28px', cursor: 'pointer' }}>×</button>
                </div>

                <div style={{ padding: '24px', display: 'grid', gap: '24px' }}>
                    {/* Story & Characteristics */}
                    {(kokki.story || kokki.characteristics) && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                            {kokki.story && <div style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '12px', padding: '16px' }}><div style={{ color: '#94a3b8', fontSize: '11px', fontWeight: '700', marginBottom: '6px', textTransform: 'uppercase' }}>📖 Story</div><div style={{ color: '#cbd5e1', fontSize: '13px', lineHeight: '1.6' }}>{kokki.story}</div></div>}
                            {kokki.characteristics && <div style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '12px', padding: '16px' }}><div style={{ color: '#94a3b8', fontSize: '11px', fontWeight: '700', marginBottom: '6px', textTransform: 'uppercase' }}>✨ Characteristics</div><div style={{ color: '#cbd5e1', fontSize: '13px', lineHeight: '1.6' }}>{kokki.characteristics}</div></div>}
                        </div>
                    )}

                    {/* Emotes */}
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                            <h3 style={{ color: '#f1f5f9', fontSize: '16px', fontWeight: '700' }}>😄 Emotes ({kokki.emotes.length})</h3>
                            <button onClick={onAddEmote} style={{ ...btnStyle(C.primary, '#0f172a'), fontSize: '12px', padding: '8px 14px' }}>➕ Add Emote</button>
                        </div>
                        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                            {kokki.emotes.length === 0 && <span style={{ color: '#64748b', fontSize: '13px' }}>No emotes yet</span>}
                            {kokki.emotes.map(em => (
                                <div key={em.id} style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '12px', padding: '12px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', minWidth: '90px', position: 'relative' }}>
                                    <img src={resolveUrl(em.imageUrl)} alt={em.name} style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '8px' }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                                    <span style={{ color: '#cbd5e1', fontSize: '12px', fontWeight: '600' }}>{em.name}</span>
                                    <button onClick={() => onDeleteEmote(em)} style={{ position: 'absolute', top: '4px', right: '4px', background: 'transparent', border: 'none', color: '#ef4444', fontSize: '14px', cursor: 'pointer', padding: '2px' }}>✕</button>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Powerups by game */}
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                            <h3 style={{ color: '#f1f5f9', fontSize: '16px', fontWeight: '700' }}>⚡ Powerups ({kokki.powerups.length}/9)</h3>
                            <button onClick={onAddPowerup} style={{ ...btnStyle(C.primary, '#0f172a'), fontSize: '12px', padding: '8px 14px' }}>➕ Assign Powerup</button>
                        </div>
                        {grouped.map(g => (
                            <div key={g.value} style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '12px', padding: '16px', marginBottom: '12px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                                    <span style={{ color: '#94a3b8', fontSize: '13px', fontWeight: '700' }}>{g.label}</span>
                                    <span style={{ color: '#475569', fontSize: '11px' }}>{g.normals.length}/2 normal • {g.ultimate ? '1' : '0'}/1 ultimate</span>
                                </div>
                                {g.normals.length === 0 && !g.ultimate && <span style={{ color: '#475569', fontSize: '12px' }}>No powerups assigned</span>}
                                <div style={{ display: 'grid', gap: '8px' }}>
                                    {g.normals.map(p => (
                                        <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px', background: '#1e293b', borderRadius: '8px' }}>
                                            {p.template.svgIcon && <img src={resolveUrl(p.template.svgIcon)} alt="" style={{ width: '28px', height: '28px' }} />}
                                            <div style={{ flex: 1 }}>
                                                <span style={{ color: '#f1f5f9', fontSize: '13px', fontWeight: '600' }}>{p.template.name}</span>
                                                <span style={{ color: '#64748b', fontSize: '11px', marginLeft: '8px' }}>×{p.charges}</span>
                                                {p.template.description && <div style={{ color: '#94a3b8', fontSize: '11px', marginTop: '2px' }}>{p.template.description}</div>}
                                                <div style={{ color: '#475569', fontSize: '10px', marginTop: '2px' }}>{p.template.effectKey}</div>
                                            </div>
                                            <input type="number" min={1} max={10} value={p.charges} onChange={(e) => onUpdateCharges(p, Math.max(1, +e.target.value))} style={{ ...inputStyle, width: '55px', padding: '4px', textAlign: 'center', fontSize: '12px' }} />
                                            <button onClick={() => onDeletePowerup(p)} style={{ background: 'transparent', border: 'none', color: C.danger, fontSize: '13px', cursor: 'pointer' }}>✕</button>
                                        </div>
                                    ))}
                                    {g.ultimate && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px', background: 'linear-gradient(90deg, #1e293b, #312e81)', borderRadius: '8px', border: '1px solid #4338ca' }}>
                                            {g.ultimate.template.svgIcon && <img src={resolveUrl(g.ultimate.template.svgIcon)} alt="" style={{ width: '28px', height: '28px' }} />}
                                            <div style={{ flex: 1 }}>
                                                <span style={{ color: '#c7d2fe', fontSize: '13px', fontWeight: '700' }}>{g.ultimate.template.name}</span>
                                                <span style={{ color: '#8b5cf6', fontSize: '11px', marginLeft: '8px' }}>⭐ ULTIMATE ×{g.ultimate.charges}</span>
                                                {g.ultimate.template.description && <div style={{ color: '#a5b4fc', fontSize: '11px', marginTop: '2px' }}>{g.ultimate.template.description}</div>}
                                                <div style={{ color: '#475569', fontSize: '10px', marginTop: '2px' }}>{g.ultimate.template.effectKey}</div>
                                            </div>
                                            {g.ultimate.template.effectGif && <img src={resolveUrl(g.ultimate.template.effectGif)} alt="fx" style={{ width: '36px', height: '36px', borderRadius: '6px' }} />}
                                            <input type="number" min={1} max={10} value={g.ultimate.charges} onChange={(e) => onUpdateCharges(g.ultimate!, Math.max(1, +e.target.value))} style={{ ...inputStyle, width: '55px', padding: '4px', textAlign: 'center', fontSize: '12px' }} />
                                            <button onClick={() => onDeletePowerup(g.ultimate!)} style={{ background: 'transparent', border: 'none', color: C.danger, fontSize: '13px', cursor: 'pointer' }}>✕</button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    {/* Skins */}
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                            <h3 style={{ color: '#f1f5f9', fontSize: '16px', fontWeight: '700' }}>🎨 Skins ({kokki.skins?.length || 0})</h3>
                            <button onClick={onAddSkin} style={{ ...btnStyle(C.primary, '#0f172a'), fontSize: '12px', padding: '8px 14px' }}>➕ Add Skin</button>
                        </div>
                        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                            {(!kokki.skins || kokki.skins.length === 0) && <span style={{ color: '#64748b', fontSize: '13px' }}>No skins yet</span>}
                            {kokki.skins?.map(sk => (
                                <div key={sk.id} style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '12px', padding: '12px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', minWidth: '100px', position: 'relative' }}>
                                    {sk.imageUrl ? <img src={resolveUrl(sk.imageUrl)} alt={sk.name} style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '8px' }} onError={(e) => { e.currentTarget.style.display = 'none'; }} /> : <div style={{ width: '60px', height: '60px', background: '#334155', borderRadius: '8px', display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: '24px' }}>🎨</div>}
                                    <span style={{ color: '#cbd5e1', fontSize: '12px', fontWeight: '600' }}>{sk.name}</span>
                                    <div style={{ display: 'flex', gap: '4px' }}>
                                        <button onClick={() => onEditSkin(sk)} style={{ background: 'transparent', border: 'none', color: C.secondary, fontSize: '13px', cursor: 'pointer' }}>✏️</button>
                                        <button onClick={() => onDeleteSkin(sk)} style={{ background: 'transparent', border: 'none', color: C.danger, fontSize: '13px', cursor: 'pointer' }}>✕</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    </div>
                </div>
            </div>
        </div>
    );
};


// ==================== MAIN PAGE ====================

export default function KokkisPage() {
    const [kokkis, setKokkis] = useState<Kokki[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    // Modals
    const [showForm, setShowForm] = useState(false);
    const [showDelete, setShowDelete] = useState(false);
    const [showDetail, setShowDetail] = useState(false);
    const [showEmoteForm, setShowEmoteForm] = useState(false);
    const [showPowerupForm, setShowPowerupForm] = useState(false);
    const [showDeleteEmote, setShowDeleteEmote] = useState(false);
    const [showDeletePowerup, setShowDeletePowerup] = useState(false);
    const [showSkinForm, setShowSkinForm] = useState(false);
    const [showDeleteSkin, setShowDeleteSkin] = useState(false);

    const [selected, setSelected] = useState<Kokki | null>(null);
    const [selectedEmote, setSelectedEmote] = useState<KokkiEmote | null>(null);
    const [selectedPowerup, setSelectedPowerup] = useState<KokkiPowerup | null>(null);
    const [selectedSkin, setSelectedSkin] = useState<KokkiSkin | null>(null);

    const load = async () => {
        try { setLoading(true); setError(null); setKokkis(await fetchAllKokkis()); }
        catch (e: any) { setError(e.message); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const flash = (msg: string) => { setSuccess(msg); setTimeout(() => setSuccess(null), 3000); };

    // Kokki CRUD handlers
    const handleCreate = () => { setSelected(null); setShowForm(true); };
    const handleEdit = (k: Kokki) => { setSelected(k); setShowForm(true); };
    const handleDeleteClick = (k: Kokki) => { setSelected(k); setShowDelete(true); };
    const handleView = (k: Kokki) => { setSelected(k); setShowDetail(true); };

    const handleSaveKokki = async (data: CreateKokkiDto | UpdateKokkiDto) => {
        try {
            setSubmitting(true); setError(null);
            if (selected) { await updateKokki(selected.id, data); flash('Kokki updated!'); }
            else { await createKokki(data as CreateKokkiDto); flash('Kokki created!'); }
            setShowForm(false); load();
        } catch (e: any) { setError(e.message); } finally { setSubmitting(false); }
    };

    const handleConfirmDelete = async () => {
        if (!selected) return;
        try {
            setSubmitting(true); setError(null);
            await deleteKokki(selected.id);
            flash('Kokki deleted!'); setShowDelete(false); setShowDetail(false); load();
        } catch (e: any) { setError(e.message); } finally { setSubmitting(false); }
    };

    // Emote handlers
    const handleSaveEmote = async (data: CreateKokkiEmoteDto) => {
        try {
            setSubmitting(true); setError(null);
            await createEmote(data);
            flash('Emote added!'); setShowEmoteForm(false); load();
            // Refresh detail
            if (selected) { const refreshed = await fetchAllKokkis(); const updated = refreshed.find(k => k.id === selected.id); if (updated) setSelected(updated); }
        } catch (e: any) { setError(e.message); } finally { setSubmitting(false); }
    };

    const handleConfirmDeleteEmote = async () => {
        if (!selectedEmote) return;
        try {
            setSubmitting(true); await deleteEmote(selectedEmote.id);
            flash('Emote deleted!'); setShowDeleteEmote(false);
            const refreshed = await fetchAllKokkis(); setKokkis(refreshed);
            if (selected) { const updated = refreshed.find(k => k.id === selected.id); if (updated) setSelected(updated); }
        } catch (e: any) { setError(e.message); } finally { setSubmitting(false); }
    };

    // Powerup handlers
    const handleAssignPowerup = async (data: CreateKokkiPowerupDto) => {
        try {
            setSubmitting(true); setError(null);
            await assignPowerup(data); flash('Powerup assigned!');
            setShowPowerupForm(false);
            const refreshed = await fetchAllKokkis(); setKokkis(refreshed);
            if (selected) { const updated = refreshed.find(k => k.id === selected.id); if (updated) setSelected(updated); }
        } catch (e: any) { setError(e.message); } finally { setSubmitting(false); }
    };

    const handleUpdateCharges = async (p: KokkiPowerup, charges: number) => {
        try {
            await updatePowerup(p.id, { charges });
            const refreshed = await fetchAllKokkis(); setKokkis(refreshed);
            if (selected) { const updated = refreshed.find(k => k.id === selected.id); if (updated) setSelected(updated); }
        } catch (e: any) { setError(e.message); }
    };

    const handleConfirmDeletePowerup = async () => {
        if (!selectedPowerup) return;
        try {
            setSubmitting(true); await deletePowerup(selectedPowerup.id);
            flash('Powerup deleted!'); setShowDeletePowerup(false);
            const refreshed = await fetchAllKokkis(); setKokkis(refreshed);
            if (selected) { const updated = refreshed.find(k => k.id === selected.id); if (updated) setSelected(updated); }
        } catch (e: any) { setError(e.message); } finally { setSubmitting(false); }
    };

    // Skin handlers
    const handleSaveSkin = async (data: CreateKokkiSkinDto | UpdateKokkiSkinDto) => {
        try {
            setSubmitting(true); setError(null);
            if (selectedSkin) { await updateSkin(selectedSkin.id, data as UpdateKokkiSkinDto); flash('Skin updated!'); }
            else { await createSkin(data as CreateKokkiSkinDto); flash('Skin added!'); }
            setShowSkinForm(false);
            const refreshed = await fetchAllKokkis(); setKokkis(refreshed);
            if (selected) { const updated = refreshed.find(k => k.id === selected.id); if (updated) setSelected(updated); }
        } catch (e: any) { setError(e.message); } finally { setSubmitting(false); }
    };

    const handleConfirmDeleteSkin = async () => {
        if (!selectedSkin) return;
        try {
            setSubmitting(true); await deleteSkin(selectedSkin.id);
            flash('Skin deleted!'); setShowDeleteSkin(false);
            const refreshed = await fetchAllKokkis(); setKokkis(refreshed);
            if (selected) { const updated = refreshed.find(k => k.id === selected.id); if (updated) setSelected(updated); }
        } catch (e: any) { setError(e.message); } finally { setSubmitting(false); }
    };

    return (
        <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)', padding: '32px' }}>
            <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
                {/* Header */}
                <div style={{ marginBottom: '32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
                    <div>
                        <h1 style={{ color: '#f1f5f9', fontSize: '32px', fontWeight: '800', marginBottom: '8px' }}>🍪 Kokki Management</h1>
                        <p style={{ color: '#64748b', fontSize: '14px' }}>{kokkis.length} kokki{kokkis.length !== 1 ? 's' : ''} • Avatars, emotes & powerups</p>
                    </div>
                    <button onClick={handleCreate} style={{ padding: '12px 20px', background: C.primary, color: '#0f172a', border: 'none', borderRadius: '12px', fontSize: '14px', cursor: 'pointer', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        ➕ Add Kokki
                    </button>
                </div>

                {/* Messages */}
                {success && <div style={{ background: '#064e3b', border: '1px solid #10b981', padding: '16px 20px', borderRadius: '12px', color: '#10b981', fontSize: '14px', marginBottom: '24px' }}>✅ {success}</div>}
                {error && <div style={{ background: '#7f1d1d', border: '1px solid #ef4444', padding: '16px 20px', borderRadius: '12px', color: '#fca5a5', fontSize: '14px', marginBottom: '24px', display: 'flex', justifyContent: 'space-between' }}><span>❌ {error}</span><button onClick={() => setError(null)} style={{ background: 'transparent', border: 'none', color: '#fca5a5', fontSize: '18px', cursor: 'pointer' }}>×</button></div>}

                {/* Grid */}
                {loading ? (
                    <div style={{ textAlign: 'center', padding: '60px', color: '#64748b' }}>⏳ Loading kokkis...</div>
                ) : kokkis.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '60px' }}>
                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>🍪</div>
                        <div style={{ color: '#64748b', fontSize: '16px', marginBottom: '24px' }}>No kokkis yet</div>
                        <button onClick={handleCreate} style={btnStyle(C.primary, '#0f172a')}>Create your first Kokki</button>
                    </div>
                ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '20px' }}>
                        {kokkis.map(k => (
                            <div key={k.id} style={{ background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', borderRadius: '16px', border: '1px solid #334155', padding: '20px', cursor: 'pointer', transition: 'border-color 0.2s' }} onClick={() => handleView(k)}>
                                <div style={{ display: 'flex', gap: '16px', alignItems: 'center', marginBottom: '14px' }}>
                                    {k.imageUrl ? <img src={resolveUrl(k.imageUrl)} alt={k.name} style={{ width: '64px', height: '64px', borderRadius: '14px', objectFit: 'cover', border: '2px solid #334155' }} onError={(e) => { e.currentTarget.style.display = 'none'; }} /> : <div style={{ width: '64px', height: '64px', background: '#334155', borderRadius: '14px', display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: '28px' }}>🍪</div>}
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <h3 style={{ color: '#f1f5f9', fontSize: '18px', fontWeight: '700', marginBottom: '4px' }}>{k.name}</h3>
                                        <p style={{ color: '#64748b', fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{k.description || 'No description'}</p>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '14px' }}>
                                    <span style={{ background: '#334155', color: '#94a3b8', padding: '4px 10px', borderRadius: '8px', fontSize: '11px', fontWeight: '600' }}>😄 {k.emotes.length} emotes</span>
                                    <span style={{ background: '#334155', color: '#94a3b8', padding: '4px 10px', borderRadius: '8px', fontSize: '11px', fontWeight: '600' }}>⚡ {k.powerups.length} powerups</span>
                                    <span style={{ background: '#334155', color: '#94a3b8', padding: '4px 10px', borderRadius: '8px', fontSize: '11px', fontWeight: '600' }}>🎨 {k.skins?.length || 0} skins</span>
                                </div>
                                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }} onClick={(e) => e.stopPropagation()}>
                                    <button onClick={() => handleEdit(k)} style={btnStyle(C.secondary)}>✏️ Edit</button>
                                    <button onClick={() => handleDeleteClick(k)} style={btnStyle(C.danger)}>🗑️ Delete</button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Modals */}
            {showForm && <KokkiFormModal kokki={selected} onSave={handleSaveKokki} onClose={() => setShowForm(false)} loading={submitting} />}
            {showDelete && selected && <DeleteModal title="Kokki" name={selected.name} onConfirm={handleConfirmDelete} onClose={() => setShowDelete(false)} loading={submitting} />}
            {showDetail && selected && (
                <KokkiDetailPanel
                    kokki={selected}
                    onAddEmote={() => setShowEmoteForm(true)}
                    onDeleteEmote={(e) => { setSelectedEmote(e); setShowDeleteEmote(true); }}
                    onAddPowerup={() => setShowPowerupForm(true)}
                    onUpdateCharges={handleUpdateCharges}
                    onDeletePowerup={(p) => { setSelectedPowerup(p); setShowDeletePowerup(true); }}
                    onAddSkin={() => { setSelectedSkin(null); setShowSkinForm(true); }}
                    onEditSkin={(s) => { setSelectedSkin(s); setShowSkinForm(true); }}
                    onDeleteSkin={(s) => { setSelectedSkin(s); setShowDeleteSkin(true); }}
                    onClose={() => setShowDetail(false)}
                />
            )}
            {showEmoteForm && selected && <AddEmoteModal kokkiId={selected.id} onSave={handleSaveEmote} onClose={() => setShowEmoteForm(false)} loading={submitting} />}
            {showPowerupForm && selected && <AssignPowerupModal kokkiId={selected.id} existingPowerups={selected.powerups} onSave={handleAssignPowerup} onClose={() => setShowPowerupForm(false)} loading={submitting} />}
            {showSkinForm && selected && <SkinFormModal kokkiId={selected.id} skin={selectedSkin} onSave={handleSaveSkin} onClose={() => setShowSkinForm(false)} loading={submitting} />}
            {showDeleteEmote && selectedEmote && <DeleteModal title="Emote" name={selectedEmote.name} onConfirm={handleConfirmDeleteEmote} onClose={() => setShowDeleteEmote(false)} loading={submitting} />}
            {showDeletePowerup && selectedPowerup && <DeleteModal title="Powerup" name={selectedPowerup.template.name} onConfirm={handleConfirmDeletePowerup} onClose={() => setShowDeletePowerup(false)} loading={submitting} />}
            {showDeleteSkin && selectedSkin && <DeleteModal title="Skin" name={selectedSkin.name} onConfirm={handleConfirmDeleteSkin} onClose={() => setShowDeleteSkin(false)} loading={submitting} />}
        </div>
    );
}
